// These MUI icons are temporary while the settings sidebar is being redesigned
import {
  AutoAwesomeOutlined as AgentLibraryIcon,
  Settings as AdminSettingsIcon,
  Receipt as BudgetApprovalsIcon,
  Flag as FeatureFlagsIcon,
  SyncAlt as EnvironmentSyncIcon,
  Translate,
} from '@mui/icons-material'
import { Box, Stack } from '@mui/material'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useRouteAccess } from '@omnea-app/fe-authz/useRouteAccess'
import { useFeatureFlags } from '@omnea-app/fe-org-settings/FeatureFlagsProvider'
import { useVisibleCatalogueSettings } from '@omnea-app/fe-settings/catalogues/useVisibleCatalogueSettings'
import { useSettingsLdFlags } from '@omnea-app/fe-settings/launch-darkly'
import { FeatureFlagsEnum } from '@omnea-app/packages/universal/feature-flags/featureFlags'
import { filterSectionsBySearch } from '../filterSectionsBySearch'
import { useIntegrationsItems } from '../hooks/useIntegrationsItems'
import { SidebarList, SidebarListItemButton, SidebarListItemText, SidebarSectionTitle } from '../StyledSidebarComponents'
import { getSettingsIconByName } from './SettingsIcon'
import {
  hrefOfMenuItem,
  isMenuItemSelected,
  isMenuItemVisible,
  recordHrefsOf,
  type SettingsMenuItem,
  type SettingsSection,
} from './settingsMenuItem'
import { SettingsSearchbar } from './SettingsSearchbar'

const PREVENT_BROWSER_URL_TOOLTIP_OVERLAP_PADDING = 14

export const SettingsSidebarContent = () => {
  const { t } = useTranslation('common')
  const router = useRouter()
  const [searchQuery, setSearchQuery] = useState('')
  const featureFlags = useFeatureFlags()
  const ldFeatureFlags = useSettingsLdFlags()
  const routeAccess = useRouteAccess()
  const integrationsItems = useIntegrationsItems(routeAccess.get('settingsIntegrations') === 'allowed')
  const hasVisibleCatalogueSettings = useVisibleCatalogueSettings().length > 0

  const personalSettingSection: SettingsSection = {
    title: t('sidebar.account-title'),
    items: [
      {
        label: t('sidebar.notifications'),
        icon: getSettingsIconByName('notifications'),
        destination: { kind: 'route', routeKey: 'settingsNotifications' },
      },
      {
        label: t('sidebar.preferences'),
        icon: getSettingsIconByName('preferences'),
        destination: { kind: 'route', routeKey: 'settingsPreferences' },
      },
    ],
  }

  const organisationSection: SettingsSection = {
    title: t('sidebar.organisation-title'),
    items: [
      {
        label: t('sidebar.default-notifications'),
        icon: getSettingsIconByName('notifications'),
        destination: { kind: 'route', routeKey: 'settingsDefaultNotifications' },
      },
      { label: t('sidebar.ai'), icon: getSettingsIconByName('ai'), destination: { kind: 'route', routeKey: 'settingsAi' } },
      {
        label: t('sidebar.security'),
        icon: getSettingsIconByName('security'),
        destination: { kind: 'route', routeKey: 'settingsSecurity' },
      },
      {
        label: t('sidebar.core-data'),
        icon: getSettingsIconByName('core-data'),
        destination: { kind: 'route', routeKey: 'settingsCoreData' },
      },
      {
        label: t('sidebar.users-and-groups'),
        icon: getSettingsIconByName('users'),
        destination: { kind: 'route', routeKey: 'settingsUsersAndGroups' },
      },
      {
        label: t('sidebar.permissions'),
        icon: getSettingsIconByName('security'),
        destination: { kind: 'route', routeKey: 'settingsPermissions' },
      },
      {
        label: t('sidebar.user-properties'),
        icon: getSettingsIconByName('user-properties'),
        destination: { kind: 'route', routeKey: 'settingsUserProperties' },
      },
      { label: t('sidebar.api'), icon: getSettingsIconByName('api'), destination: { kind: 'route', routeKey: 'settingsApi' } },
      {
        label: t('sidebar.import'),
        icon: getSettingsIconByName('import'),
        destination: { kind: 'route', routeKey: 'settingsImport' },
      },
      {
        label: t('sidebar.audit-log'),
        icon: getSettingsIconByName('audit-log'),
        destination: { kind: 'route', routeKey: 'settingsAuditLog' },
      },
      {
        label: t('sidebar.external-archive'),
        icon: getSettingsIconByName('audit-log'),
        destination: { kind: 'route', routeKey: 'settingsExternalArchive' },
        showsOmneanOnlyBadge: true,
      },
    ],
  }

  const featuresSection: SettingsSection = {
    title: t('sidebar.features-title'),
    items: [
      {
        label: t('sidebar.agent-library'),
        icon: <AgentLibraryIcon sx={{ width: 18 }} />,
        destination: { kind: 'route', routeKey: 'settingsAgentLibrary' },
      },
      {
        label: t('sidebar.workflows'),
        icon: getSettingsIconByName('workflows'),
        destination: { kind: 'route', routeKey: 'settingsWorkflows' },
      },
      {
        label: t('sidebar.budget-approvals'),
        icon: <BudgetApprovalsIcon sx={{ width: 18 }} />,
        destination: { kind: 'route', routeKey: 'settingsDynamicAssignees' },
      },
      {
        label: t('sidebar.forms'),
        icon: getSettingsIconByName('questions'),
        destination: { kind: 'route', routeKey: 'settingsForms' },
      },
      {
        label: t('sidebar.sourcing'),
        icon: getSettingsIconByName('sourcing'),
        destination: { kind: 'route', routeKey: 'settingsSourcing' },
      },
      {
        label: t('sidebar.auctions'),
        icon: getSettingsIconByName('auctions'),
        destination: { kind: 'route', routeKey: 'settingsAuctions' },
      },
      {
        label: t('sidebar.risk-management'),
        icon: getSettingsIconByName('risk-management'),
        destination: { kind: 'route', routeKey: 'settingsRisks' },
      },
      // Which catalogue kinds an organisation has is data, not a route rule, so it stays an item condition.
      ...(hasVisibleCatalogueSettings
        ? [
            {
              label: t('sidebar.catalogues'),
              icon: getSettingsIconByName('catalogues'),
              destination: { kind: 'route', routeKey: 'settingsCatalogues' } as const,
            },
          ]
        : []),
      {
        label: t('sidebar.product-supplier-categories'),
        icon: getSettingsIconByName('product-supplier-categories'),
        destination: { kind: 'route', routeKey: 'settingsCategories' },
      },
      {
        label: ldFeatureFlags.SHOW_DOCUMENT_REVIEW ? t('sidebar.document-review-playbooks') : t('sidebar.document-storage'),
        icon: getSettingsIconByName('document-storage'),
        destination: { kind: 'route', routeKey: 'settingsDocumentStorage' },
      },
      {
        label: t('sidebar.policies'),
        icon: getSettingsIconByName('policies'),
        destination: { kind: 'route', routeKey: 'settingsPolicies' },
      },
      {
        label: t('sidebar.environment-sync'),
        icon: <EnvironmentSyncIcon sx={{ width: 18, height: 18 }} />,
        destination: { kind: 'route', routeKey: 'settingsEnvironmentSync' },
      },
      {
        label: t('sidebar.feature-flags', 'Feature flags'),
        icon: <FeatureFlagsIcon sx={{ width: 18, height: 18 }} />,
        destination: { kind: 'route', routeKey: 'settingsInternalFeatureFlags' },
        showsOmneanOnlyBadge: true,
      },
      {
        label: t('sidebar.localisation'),
        icon: <Translate sx={{ width: 18, height: 18 }} />,
        destination: { kind: 'route', routeKey: 'settingsLocalisation' },
      },
      {
        label: t('sidebar.admin-settings'),
        icon: <AdminSettingsIcon sx={{ width: 18, height: 18 }} />,
        destination: { kind: 'route', routeKey: 'settingsAdmin' },
      },
      // The migration tool exists to migrate organisations onto agreements, so the flag hides the item
      // for those already on them rather than gating the route.
      ...(featureFlags[FeatureFlagsEnum.CREATE_AGREEMENT] === true
        ? [
            {
              label: t('sidebar.agreements-migration'),
              icon: <AdminSettingsIcon sx={{ width: 18, height: 18 }} />,
              destination: { kind: 'route', routeKey: 'settingsAgreementsMigration' } as const,
              showsOmneanOnlyBadge: true,
            },
          ]
        : []),
    ],
  }

  const accountsPayableSection: SettingsSection = {
    title: t('sidebar.accounts-payable-title'),
    items: [
      {
        label: t('sidebar.goods-receipting'),
        icon: getSettingsIconByName('goods-receipting'),
        destination: { kind: 'route', routeKey: 'settingsGoodsReceipting' },
      },
      {
        label: t('sidebar.invoice-processing'),
        icon: getSettingsIconByName('invoice-processing'),
        destination: { kind: 'route', routeKey: 'settingsInvoiceProcessing' },
      },
    ],
  }

  const integrationsSection: SettingsSection = {
    title: t('sidebar.integrations'),
    items: integrationsItems,
  }

  const sections: SettingsSection[] = [
    personalSettingSection,
    organisationSection,
    featuresSection,
    accountsPayableSection,
    integrationsSection,
  ]

  const permissionFilteredSections = sections
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => isMenuItemVisible(item, routeAccess)),
    }))
    .filter((section) => section.items.length > 0)

  const filteredSections = filterSectionsBySearch(permissionFilteredSections, searchQuery)
  // Unfiltered: a record owns its address whether or not a search, or a slow load, is hiding its row.
  const recordHrefs = recordHrefsOf(sections)

  return (
    <Stack spacing={3} sx={{ height: '100%', overflow: 'hidden' }}>
      {/* Menu sections */}
      <Stack
        spacing={2}
        sx={{
          pb: PREVENT_BROWSER_URL_TOOLTIP_OVERLAP_PADDING,
          overflowY: 'auto',
          overflowX: 'hidden',
        }}
      >
        <SettingsSearchbar searchQuery={searchQuery} onSearchChange={setSearchQuery} />
        {filteredSections.map((section: SettingsSection) => (
          <Box key={section.title}>
            <SidebarSectionTitle>{section.title}</SidebarSectionTitle>
            <SidebarList>
              {section.items.map((item: SettingsMenuItem) => {
                const href = hrefOfMenuItem(item)
                const newTabProps = item.destination.kind === 'external' ? { target: '_blank', rel: 'noopener noreferrer' } : {}
                return (
                  <Link key={href} href={href} passHref {...newTabProps}>
                    <SidebarListItemButton selected={isMenuItemSelected(item, router.asPath, recordHrefs)}>
                      <Box sx={{ minWidth: 24, color: 'fg.muted', mr: 2, mt: 1 }}>{item.icon}</Box>
                      <SidebarListItemText
                        primary={item.label}
                        secondary={item.showsOmneanOnlyBadge === true ? t('sidebar.omnean-only') : undefined}
                      />
                    </SidebarListItemButton>
                  </Link>
                )
              })}
            </SidebarList>
          </Box>
        ))}
      </Stack>
    </Stack>
  )
}
